import type { CartSuggestionCardsPreviewProduct } from './cart-suggestion-cards-block.config';
import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

/** Serializable product row used in CB2 kiosk suggestions modal preview scenarios. */
export type KioskSuggestionsModalPreviewProduct = CartSuggestionCardsPreviewProduct;

/**
 * Serializable preview VM for `KioskSuggestionsModalBlockComponent`.
 * Controls whether the suggestions modal is open in CB2 studio preview,
 * and which suggestion products to render when open.
 */
export type KioskSuggestionsModalBlockScenarioContent = {
  isOpen: boolean;
  suggestions: readonly KioskSuggestionsModalPreviewProduct[];
};

export const KIOSKSUGGESTIONSMODALBLOCK_LEGACY_FIELD_ORDER = [
  "heading",
  "button_background_color",
  "button_text_color",
  "button_label",
  { type: 'separator' },
  "background_color",
  "text_color",
  { type: 'separator' },
  "card_color",
  "suggestions_set"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskSuggestionsModalBlockDescriptor: _generatedKioskSuggestionsModalBlockDescriptor,
  kioskSuggestionsModalBlockBlocDefinition,
  kioskSuggestionsModalBlockDashboardLayout,
  kioskSuggestionsModalBlockConfig,
  kioskSuggestionsModalBlockResolveConfig,
  kioskSuggestionsModalBlockProperties,
  kioskSuggestionsModalBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Kiosk Suggestion Modal Block",
    componentType: 'KioskSuggestionsModalBlockComponent',
    category: 'kiosk',
    description: "Kiosk Suggestion Modal Block.",
    keywords: [],
    allowedIn: [
      { bloc: 'KioskCartPageComponent', slot: 'body' },
    ],


    sharedFields: {}
  },

  () => ({
    heading: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.kiosk-suggestions.heading.label",
      }
    }),
    button_background_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-600"},
      options: {
        label: "pages.blocks.kiosk-suggestions.button-background-color.label",
      }
    }),
    button_text_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-50"},
      options: {
        label: "pages.blocks.kiosk-suggestions.button-text-color.label",
      }
    }),
    button_label: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.kiosk-suggestions.button-label.label",
      }
    }),
    background_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#FFFFFF"},
      options: {
        label: "pages.blocks.kiosk-suggestions.background-color.label",
      }
    }),
    text_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#000"},
      options: {
        label: "pages.blocks.kiosk-suggestions.text-color.label",
      }
    }),
    card_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#FFFFFF"},
      options: {
        label: "pages.blocks.kiosk-suggestions.card-color.label",
      }
    }),
    suggestions_set: propertyValue({
      type: 'suggestions_set_picker',
      value: '',
      options: {
        label: "pages.blocks.kiosk-suggestions.suggestions-set.label",
        help: "pages.blocks.kiosk-suggestions.suggestions-set.help",
      }
    })
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.kiosk-suggestions.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.kiosk-suggestions.fieldset.heading.label",
            fields: ["heading", "text_color"],
          },
          {
            legend: "pages.blocks.kiosk-suggestions.fieldset.modal.label",
            fields: ["background_color", "card_color"],
          },
          {
            legend: "pages.blocks.kiosk-suggestions.fieldset.button.label",
            fields: ["button_label", "button_background_color", "button_text_color"],
          },
          {
            legend: "pages.blocks.kiosk-suggestions.fieldset.suggestions.label",
            fields: ["suggestions_set"],
          },
        ],
      },
    ],
  })
);

export function kioskSuggestionsModalBlockDescriptor(): IBlockDescriptor;
export function kioskSuggestionsModalBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskSuggestionsModalBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskSuggestionsModalBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedKioskSuggestionsModalBlockDescriptor, kioskSuggestionsModalBlockProperties, fieldOrder);
}

export {
  kioskSuggestionsModalBlockBlocDefinition,
  kioskSuggestionsModalBlockDashboardLayout,
  kioskSuggestionsModalBlockProperties,
  kioskSuggestionsModalBlockConfig,
  kioskSuggestionsModalBlockResolveConfig,
  kioskSuggestionsModalBlockDefinitionSetupScenarios,
};

export type KioskSuggestionsModalBlockConfig = typeof kioskSuggestionsModalBlockConfig;
